// Global debug helper functions
// These are automatically available in browser console

import { logger } from './logger';

declare global {
  interface Window {
    __showCDNDebug: () => void;
    __hideCDNDebug: () => void;
    __showExpoDebug: () => void;
    __hideExpoDebug: () => void;
    __getExpoStatus: () => any;
    __debugStatus: () => void;
    __help: () => void;
    __wpPlugin: () => void;
    __wpPluginAdmin: () => void;
    __affiliate: () => void;
    __testVideoCall: (roomId?: string, fromName?: string) => void;
  }
}

// Initialize debug helpers
export function initDebugHelpers() {
  // Help command
  (window as any).__help = () => {
    console.log('%c🔍 Debug Commands Available', 'font-size: 16px; font-weight: bold; color: #0066cc;');
    console.log('');
    console.log('%cCDN Debug:', 'font-weight: bold; color: #00aa00;');
    console.log('  __showCDNDebug()  - Open CDN debug panel');
    console.log('  __hideCDNDebug()  - Close CDN debug panel');
    console.log('');
    console.log('%cExpo Token Debug:', 'font-weight: bold; color: #00aa00;');
    console.log('  __showExpoDebug() - Open Expo token debug panel');
    console.log('  __hideExpoDebug() - Close Expo token debug panel');
    console.log('  __getExpoStatus() - Get current token status');
    console.log('');
    console.log('%cQuick Status:', 'font-weight: bold; color: #00aa00;');
    console.log('  __debugStatus()   - Show everything at once');
    console.log('');
    console.log('%cManual Checks:', 'font-weight: bold; color: #00aa00;');
    console.log('  window.isNativeApp         - Check if Expo app');
    console.log('  window.expoPushToken       - Current push token');
    console.log('  window.requestPushToken()  - Request new token');
    console.log('');
    console.log('%cEnvironment Variables:', 'font-weight: bold; color: #00aa00;');
    console.log('  import.meta.env.VITE_CDN_URL - CDN URL');
    console.log('  import.meta.env.VITE_API_URL - API URL');
    console.log('');
    console.log('%cWordPress Plugin:', 'font-weight: bold; color: #00aa00;');
    console.log('  __wpPlugin()      - Open WordPress Plugin download');
    console.log('  __wpPluginAdmin() - Open WP Plugin admin (admin only)');
    console.log('  __affiliate()     - Open Referral & Wallet');
    console.log('');
    console.log('%cVideo Call Testing:', 'font-weight: bold; color: #00aa00;');
    console.log('  __testVideoCall()           - Test incoming call notification');
    console.log('  __testVideoCall(roomId, name) - Test with custom data');
    console.log('');
    console.log('%cFor more info, see: /md-files/DEBUG-COMMANDS.md', 'color: #666;');
  };

  // WordPress Plugin commands
  (window as any).__wpPlugin = () => {
    console.log('%c🔌 Opening WordPress Plugin Download...', 'font-weight: bold; color: #0066cc;');
    window.location.href = '/wordpress-plugin';
  };

  (window as any).__wpPluginAdmin = () => {
    console.log('%c🔌 Opening WordPress Plugin Admin...', 'font-weight: bold; color: #0066cc;');
    window.location.href = '/wordpress-plugin-admin';
  };

  (window as any).__affiliate = () => {
    console.log('%c💰 Opening Affiliate/Referral Section...', 'font-weight: bold; color: #0066cc;');
    window.location.href = '/refer-earn';
  };

  // Test video call notification
  (window as any).__testVideoCall = (roomId?: string, fromName?: string) => {
    console.log('%c🔔📞 [TEST] Testing Video Call Notification', 'font-size: 14px; font-weight: bold; color: #ff0066;');
    console.log('');
    console.log('%cDispatching incomingVideoCallFromNotification event...', 'font-weight: bold;');
    
    const testEventDetail = {
      roomId: roomId || 'test-room-' + Date.now(),
      from: 'test-user-' + Date.now(),
      fromName: fromName || 'Test User',
      fromPhoto: null,
      isVideoCall: true
    };
    
    console.log('Event detail:', testEventDetail);
    
    const testEvent = new CustomEvent('incomingVideoCallFromNotification', {
      detail: testEventDetail,
      bubbles: true,
      cancelable: true
    });
    
    const dispatched = window.dispatchEvent(testEvent);
    
    console.log('');
    console.log(`Event dispatched: ${dispatched ? '✅ Yes' : '❌ No (was handled)'}`);
    console.log('');
    console.log('%cIf the modal appears, the event listener is working!', 'color: #00aa00;');
    console.log('%cIf not, check:', 'color: #ff6600;');
    console.log('  1. IncomingCallNotification component is mounted');
    console.log('  2. Event listener is registered (check console logs)');
    console.log('  3. No errors in console');
    console.log('');
  };

  // Quick status check
  (window as any).__debugStatus = () => {
    console.log('%c📊 Current Status', 'font-size: 16px; font-weight: bold; color: #0066cc;');
    console.log('');
    
    // CDN Status
    const cdnUrl = import.meta.env.VITE_CDN_URL;
    const hasCDN = !!cdnUrl;
    console.log('%cCDN Configuration:', 'font-weight: bold;');
    console.log(`  Configured: ${hasCDN ? '✅ Yes' : '❌ No'}`);
    if (hasCDN) {
      console.log(`  URL: ${cdnUrl}`);
    }
    console.log(`  Images on page: ${document.querySelectorAll('img').length}`);
    console.log(`  CloudFront images: ${document.querySelectorAll('img[src*="cloudfront"]').length}`);
    console.log(`  S3 images: ${document.querySelectorAll('img[src*="s3"]').length}`);
    console.log('');
    
    // CORS Warning
    const s3Images = document.querySelectorAll('img[src*="s3"]').length;
    if (s3Images > 0) {
      console.log('%c⚠️  CORS Notice:', 'font-weight: bold; color: #ff6600;');
      console.log('  You have S3 direct URLs. These may have CORS restrictions.');
      console.log('  Configure S3 CORS - see: /md-files/S3-CORS-CONFIGURATION.md');
      console.log('');
    }
    
    // Expo Token Status
    const isNativeApp = !!(window as any).isNativeApp;
    const hasToken = !!(window as any).expoPushToken;
    const storedToken = localStorage.getItem('pending_expo_push_token');
    
    console.log('%cExpo Token Status:', 'font-weight: bold;');
    console.log(`  Native App: ${isNativeApp ? '✅ Yes' : '❌ No (Web)'}`);
    console.log(`  Token Available: ${hasToken ? '✅ Yes' : '❌ No'}`);
    if (hasToken) {
      console.log(`  Token: ${((window as any).expoPushToken || '').substring(0, 30)}...`);
    }
    if (storedToken) {
      console.log(`  Stored Token (pending): ${storedToken.substring(0, 30)}...`);
    }
    console.log('');
    
    // Environment
    console.log('%cEnvironment:', 'font-weight: bold;');
    console.log(`  API URL: ${import.meta.env.VITE_API_URL}`);
    console.log(`  Mode: ${import.meta.env.MODE}`);
    console.log(`  Production: ${import.meta.env.PROD}`);
    console.log('');
    
    // User Status
    const authToken = localStorage.getItem('token');
    console.log('%cUser Status:', 'font-weight: bold;');
    console.log(`  Logged In: ${authToken ? '✅ Yes' : '❌ No'}`);
    console.log('');
    
    // Recommendations
    console.log('%cRecommendations:', 'font-weight: bold; color: #0066cc;');
    if (!hasCDN) {
      console.log('  ⚠️  CDN not configured. Add VITE_CDN_URL to .env');
    }
    if (!isNativeApp) {
      console.log('  ℹ️  Not in Expo app - token registration only works in native app');
    }
    if (isNativeApp && !hasToken) {
      console.log('  ⚠️  No token available. Try: window.requestPushToken()');
    }
    if (s3Images > 0 && hasCDN) {
      console.log('  ⚠️  Some images still using S3 direct URLs');
      console.log('      Fix CORS: See /md-files/S3-CORS-CONFIGURATION.md');
    }
    console.log('');
    console.log('For detailed testing, use: __showCDNDebug() or __showExpoDebug()');
  };

  // Log availability
  logger.log('🔍 Debug helpers loaded. Type __help() for commands');
  logger.log('Quick status check: __debugStatus()');
}

// Export for use in App.tsx
export default initDebugHelpers;